import { RedoButton } from "@redotech/arbiter/buttons/redo-button";
import { Flex } from "@redotech/arbiter/flex";
import { RedoIncrementDecrement } from "@redotech/arbiter/increment-decrement/redo-increment-decrement";
import { Text } from "@redotech/arbiter/text";
import { InfoTooltip } from "@redotech/arbiter/tooltip/info-tooltip";
import { CarrierAndService } from "@redotech/redo-fulfillment-carrier-common/carriers-and-services";
import {
  LogoSize,
  ShippingLogo,
} from "@redotech/redo-web/fulfillment/common/shipping-logo";
import { memo } from "react";
import { DynamicRatePricingBenchmarkPicker } from "./dynamic-rate-pricing-benchmark-picker";
import * as styles from "./dynamic-rate-pricing-rule-row.module.css";
import {
  PricingServiceGroup,
  serviceKey,
} from "./dynamic-rate-pricing-service-groups";
import type { DynamicRatePricingRuleValue } from "./dynamic-rate-pricing-settings-form";

export interface DynamicRatePricingRuleRowProps {
  benchmarkOptions: CarrierAndService[];
  benchmarksDisabled: boolean;
  group: PricingServiceGroup;
  value: DynamicRatePricingRuleValue;
  mixed: boolean;
  disabled: boolean;
  onChange(value: DynamicRatePricingRuleValue | undefined): void;
}

export const DynamicRatePricingRuleRow = memo(
  function DynamicRatePricingRuleRow({
    benchmarkOptions,
    benchmarksDisabled,
    group,
    value,
    mixed,
    disabled,
    onChange,
  }: DynamicRatePricingRuleRowProps) {
    const options = benchmarkOptions.filter(
      (option) =>
        !group.services.some(
          (source) => serviceKey(source) === serviceKey(option),
        ),
    );
    return (
      <Flex
        borderColor="secondary"
        borderRadius="md"
        borderStyle="solid"
        borderWidth="1px"
        dir="column"
        gap="sm"
        p="lg"
      >
        <Flex align="center" className={styles.mapping} display="grid" gap="lg">
          <Flex align="center" gap="md">
            <ShippingLogo carrier={group.carrier} size={LogoSize.SMALL} />
            <Flex dir="column" gap="xs">
              <Text fontSize="sm" fontWeight="medium">
                {group.label}
              </Text>
            </Flex>
          </Flex>
          <Text fontSize="sm" textColor="secondary">
            →
          </Text>
          <Flex className={styles.settings} dir="column" gap="md" minW="none">
            <DynamicRatePricingBenchmarkPicker
              disabled={disabled || benchmarksDisabled}
              onChange={(benchmarks) => onChange({ ...value, benchmarks })}
              options={options}
              value={value.benchmarks}
            />
          </Flex>
          <Flex className={styles.savings} dir="column" gap="sm">
            <Flex
              align="center"
              className={styles.range}
              display="grid"
              gap="md"
            >
              <RedoIncrementDecrement
                ariaLabel={`${group.label} minimum savings`}
                decimalPlaces={2}
                disabled={disabled}
                fullWidth
                min={0.01}
                prefix="$"
                setValue={(minBeatRateBy) =>
                  onChange({ ...value, minBeatRateBy })
                }
                size="sm"
                step={0.01}
                value={value.minBeatRateBy}
              />
              <RedoIncrementDecrement
                ariaLabel={`${group.label} maximum savings`}
                decimalPlaces={2}
                disabled={disabled}
                fullWidth
                min={0.01}
                prefix="$"
                setValue={(maxBeatRateBy) =>
                  onChange({ ...value, maxBeatRateBy })
                }
                size="sm"
                step={0.01}
                value={value.maxBeatRateBy}
              />
              <InfoTooltip
                boxSize={16}
                tooltip={{
                  title:
                    "Savings below the cheapest selected benchmark, in USD. The left amount is the minimum; the right amount is the maximum. Prices never go below Redo cost.",
                }}
              />
            </Flex>
          </Flex>
          <RedoButton
            className={styles.ignore}
            disabled={disabled}
            hierarchy="tertiary"
            onClick={() => onChange(undefined)}
            size="xs"
            text="Ignore"
          />
        </Flex>
        {mixed && (
          <Flex align="center" gap="sm">
            <Text fontSize="xs" textColor="warning">
              Saved variants differ. Editing applies this mapping and savings to
              every variant.
            </Text>
            <RedoButton
              disabled={disabled}
              hierarchy="secondary"
              onClick={() => onChange(value)}
              size="xs"
              text="Apply to all variants"
            />
          </Flex>
        )}
      </Flex>
    );
  },
);
